/*Package importing*/
import React, { Fragment, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

/*Import custom components and assets*/

const NAVIGATION_ITEMS = [
    {
      name: "App users",
      short_name: "app_user",
      children: [
        {
          name: "Active users",
          link: "active-users"
        },
        {
          name: "Blocked users",
          link: "blocked-users"
        },
        {
          name: "Pending approvals",
          link: "pending-approvals"
        },
        {
          name: "Deactivated users",
          link: "deactivated-users"
        },
        {
          name: "Documents Expiring",
          link: "expiring-documents",
          expiryAdminOnly: true
        },
        {
          name: "Document Requests",
          link: "document-requests",
          expiryAdminOnly: true
        }
      ]
    },
    {
      name: "Transactions",
      children: [
        {
          name: "All Transactions",
          link: "transactions"
        },
        {
          name: "Scheduled Transactions",
          link: "scheduled-transactions"
        },
        {
          name: "Wallet Statement",
          link: "mini-statements"
        },
        {
          name: "Inbound Transactions",
          link: "inbound-transactions"
        },
        {
          name: "Stuck Transactions",
          link: "stuck-transactions",
          stuckTxnOnly: true
        }
      ],
      short_name: "transaction_management",
    },
    {
      name: "PG Transactions",
      short_name: "pg_transactions",
      children: [
        {
          name: "All Transactions",
          link: "pg-transactions"
        },
        {
          name: "Failed Top-ups Review",
          link: "failed-pg-review",
          godAdminOnly: true
        }
      ],
    },
    {
      name: "Beneficiaries",
      children: [
        {
          name: "International Beneficiaries",
          link: "beneficiaries"
        },
        {
          name: "Blocked Beneficiaries",
          link: "blocked-beneficiaries"
        },
        {
          name: "Domestic Beneficiaries",
          link: "domestic-beneficiaries"
        }
      ],
      short_name: "beneficiaries",
    },
    {
      name: "Support Tickets",
      children: [],
      short_name: "support_tickets",
      link: "tickets"
    },
    {
      name: "Tranna Console",
      children: [
        {
          name: "Agent Management",
          link: "agent-management"
        },
        {
          name: "Face to Face Verification",
          link: "face-to-face-verification"
        },
        {
          name: "Tranna Summary Report",
          link: "tranna-summary-report"
        },
        {
          name: "Dealer Lookup",
          link: "dealer-lookup",
          dealerLookupOnly: true
        },
        {
          name: "User Lookup",
          link: "user-lookup",
          godAdminOnly: true
        },
        {
          name: "Lookup Hub",
          link: "lookup-hub",
          godAdminOnly: true
        }
      ],
      short_name: "tranna_console",
    },
    {
      name: "Compliance Reports",
      children: [
        {
          name: "Customer Report",
          link: "customer-report"
        },
        {
          name: "AML Reports",
          link: "aml"
        },
        {
          name: "Monitored Transactions",
          link: "monitored-transactions"
        }
      ],
      short_name: "reports",
    },
    {
      name: "App settings",
      children: [
        {
          name: "Commissions & Limit",
          link: "commission-reset"
        },
        {
          name: "Legal Terms",
          link: "legal-terms"
        },
        {
          name: "Send Notifications",
          link: "send-notification"
        },
        {
          name: "Cash Channel Config",
          link: "cash-channeling"
        }
      ],
      short_name: "app_settings",
    },
    {
      name: "Admin settings",
      children: [
        {
          name: "Admin Users",
          link: "admin-users"
        },
        {
          name: "Manage Dashboard Cards",
          link: "dashboard-manage-cards",
          superAdminOnly: true
        }
      ],
      short_name: "admin_settings",
    }
];

// "My Account" section is rendered unconditionally outside the permission-
// gated NAVIGATION_ITEMS loop. Every logged-in admin can manage their own
// passkeys and password regardless of their module permissions.
const MY_ACCOUNT_ITEMS = [
  { name: "My Passkeys", link: "my-passkeys" },
  { name: "Change Password", link: "change-password" },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeItem = location.pathname.split("/").pop();

  const EXPIRY_ALLOWED_EMAILS = ['jewel@zappit.software', 'david@zappit.software'];
  const DEALER_LOOKUP_ALLOWED_EMAILS = [
    'david@zappit.software',
    'jewel@zappit.software',
    'anurag.sharma@majantel.com',
    'ramnaz.kp@majantel.com',
    'faheem.ahamed@majantel.com',
  ];
  // Same 4-person whitelist as Dealer Lookup today, kept as a separate
  // constant so it can diverge later without touching the other flag.
  const STUCK_TXN_ALLOWED_EMAILS = [
    'david@zappit.software',
    'jewel@zappit.software',
    'anurag.sharma@majantel.com',
    'ramnaz.kp@majantel.com',
  ];
  // Mirrors src/containers/godAdmin/main.js ALLOWED_EMAILS exactly.
  // Bank-action team that can use Failed PG Top-ups Review + User Lookup
  // without needing access_level=100 (BE has matching list).
  const GOD_ADMIN_ALLOWED_EMAILS = [
    'jewel@zappit.software',
    'david@zappit.software',
    'sadiq.allawati@banknizwa.om',
    'mohamed.alrashdi@banknizwa.om',
  ];

  const { short_names, isSuperAdmin, isExpiryAdmin, isDealerLookupAdmin, isStuckTxnAdmin, isGodAdmin } = useMemo(() => {
    const permissions = sessionStorage.getItem("permissions");
    const names = permissions ? JSON.parse(permissions).map((item) => item.short_name) : [];
    const accessLevel = sessionStorage.getItem("accessLevel");
    const adminData = sessionStorage.getItem("data");
    const adminEmail = adminData ? JSON.parse(adminData).email : null;
    const normalizedEmail = adminEmail ? adminEmail.toLowerCase() : null;
    const isSuper = !!(accessLevel && parseInt(accessLevel) === 100);
    return {
      short_names: names,
      isSuperAdmin: isSuper,
      isExpiryAdmin: normalizedEmail && EXPIRY_ALLOWED_EMAILS.includes(normalizedEmail),
      isDealerLookupAdmin: normalizedEmail && DEALER_LOOKUP_ALLOWED_EMAILS.includes(normalizedEmail),
      isStuckTxnAdmin: normalizedEmail && STUCK_TXN_ALLOWED_EMAILS.includes(normalizedEmail),
      isGodAdmin: isSuper || (normalizedEmail && GOD_ADMIN_ALLOWED_EMAILS.includes(normalizedEmail)),
    };
  }, []);

  const expandSubItem = (firstChildLink) => {
    if (firstChildLink) {
      navigate(`/${firstChildLink}`);
    }
  };

  // Hide the Dashboard link for admins with no module permissions. A freshly
  // invited admin has an empty permissions array and shouldn't see the
  // dashboard cards (which include transaction counts and other privileged
  // numbers). Super-admins and god-admins keep access regardless.
  const canSeeDashboard = isSuperAdmin || isGodAdmin || short_names.length > 0;

  return (
    <div className="col-lg-2 position-relative e-sidebar-wrap">
      {canSeeDashboard && (
        <Link to="/dashboard" className={`e-sidebar-item e-sidebar-dash${activeItem === "dashboard" ? " e-active" : ""}`}>Dashboard</Link>
      )}

      {
        NAVIGATION_ITEMS.map((item, index) => {
          if (item.superAdminOnly && !isSuperAdmin) {
            return null;
          }
          if (item.godAdminOnly && !isGodAdmin) {
            return null;
          }
          return (
            item.children.length > 0 ?
              <Fragment key={index}>
                {
                  short_names.includes(item.short_name) &&
                    <div
                      className={`e-sidebar-item e-sidebar-multi-item e-sidebar-appSettings${item.children.some((child) => child.link === activeItem) || activeItem === item.link ? " e-active" : ""}`}
                      onClick={() => expandSubItem(item.children[0]?.link)}
                    >{item.name}</div>
                }

                {
                  item.children.some((child) => child.link === activeItem) || activeItem === item.link ?
                    <div className="e-sidebar-sub-items-wrap">
                      {
                        item.children.map((child, childIndex) => {
                          if (child.superAdminOnly && !isSuperAdmin) {
                            return null;
                          }
                          if (child.godAdminOnly && !isGodAdmin) {
                            return null;
                          }
                          if (child.expiryAdminOnly && !isExpiryAdmin) {
                            return null;
                          }
                          if (child.dealerLookupOnly && !isDealerLookupAdmin) {
                            return null;
                          }
                          if (child.stuckTxnOnly && !isStuckTxnAdmin) {
                            return null;
                          }
                          return (
                            <Link key={childIndex} to={`/${child.link}`} className={`e-sidebar-sub-item${activeItem === child.link ? " e-active" : ""}`}>{child.name}</Link>
                          )
                        })
                      }
                    </div>
                    :
                    null
                }
              </Fragment>
              :
              short_names.includes(item.short_name) &&
                <Link key={index} to={`/${item.link}`} className={`e-sidebar-item e-sidebar-trans${activeItem === item.link ? " e-active" : ""}`}>{item.name}</Link>
          )
        })
      }

      {/* My Account — always visible, no permission gate. */}
      <div
        className={`e-sidebar-item e-sidebar-multi-item e-sidebar-appSettings${MY_ACCOUNT_ITEMS.some((c) => c.link === activeItem) ? " e-active" : ""}`}
        onClick={() => navigate(`/${MY_ACCOUNT_ITEMS[0].link}`)}
      >My Account</div>
      {MY_ACCOUNT_ITEMS.some((c) => c.link === activeItem) && (
        <div className="e-sidebar-sub-items-wrap">
          {MY_ACCOUNT_ITEMS.map((c, i) => (
            <Link
              key={i}
              to={`/${c.link}`}
              className={`e-sidebar-sub-item${activeItem === c.link ? " e-active" : ""}`}
            >{c.name}</Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
